/**
 * Admin Leads RPC definitions
 *
 * The CRM funnel-head RpcGroup. A lead = a person with no customer/intake yet.
 * `create`/`list`/`get`/`update` manage the funnel; `convert` runs the lead →
 * intake conversion. All endpoints require admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Person } from "@fm/db/frontend";
import { CustomerType } from "@fm/db/models/Customer";
import { Intake, IntakeSource } from "@fm/db/models/Intake";
import { Lead, LeadSource, LeadStatus } from "@fm/db/models/Lead";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValidationError } from "../errors.ts";

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * A lead joined with the person it represents — the `get` detail shape.
 *
 * @since 1.0.0
 */
export const LeadWithPerson = Schema.Struct({
  lead: Lead.json,
  person: Person.json,
});

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input for `create` — the new person to register (clerk_id NULL) plus the
 * lead's acquisition channel. The lead is always seeded with `status = 'new'`;
 * NO customer or intake is created.
 *
 * @since 1.0.0
 */
export const LeadCreateInput = Schema.Struct({
  person: Schema.Struct({
    name: Schema.NonEmptyTrimmedString,
    email: Schema.NonEmptyTrimmedString,
    phone: Schema.optional(Schema.NullOr(Schema.String)),
  }),
  source: LeadSource,
  sourceDetail: Schema.optional(Schema.NullOr(Schema.String)),
  assignedToPersonId: Schema.optional(Schema.NullOr(Schema.String)),
  notes: Schema.optional(Schema.NullOr(Schema.String)),
});

/**
 * Input for `list` — both filters are optional and AND-ed together.
 *
 * @since 1.0.0
 */
export const LeadListInput = Schema.Struct({
  status: Schema.optional(LeadStatus),
  assignedToPersonId: Schema.optional(Schema.NonEmptyTrimmedString),
});

/**
 * Input for `get` — lead id.
 *
 * @since 1.0.0
 */
export const LeadByIdInput = Schema.Struct({
  id: Schema.NonEmptyTrimmedString,
});

/**
 * Input for `update` — lead id plus any subset of mutable fields. `status` is
 * carried as a plain string so an unknown status surfaces as a typed
 * `ValidationError` from the handler rather than a wire-level decode failure.
 *
 * @since 1.0.0
 */
export const LeadUpdateInput = Schema.Struct({
  id: Schema.NonEmptyTrimmedString,
  status: Schema.optional(Schema.String),
  assignedToPersonId: Schema.optional(Schema.NullOr(Schema.String)),
  notes: Schema.optional(Schema.NullOr(Schema.String)),
});

/**
 * Input for `convert` — the new customer's identity plus the intake's
 * acquisition channel. The lead's EXISTING person is reused (no duplicate);
 * `intakeSource` defaults to the lead's own source when omitted.
 *
 * @since 1.0.0
 */
export const LeadConvertInput = Schema.Struct({
  leadId: Schema.NonEmptyTrimmedString,
  customer: Schema.Struct({
    type: CustomerType,
    name: Schema.NonEmptyTrimmedString,
    resellerId: Schema.optional(Schema.NullOr(Schema.String)),
  }),
  intakeSource: Schema.optional(IntakeSource),
  intakeSourceDetail: Schema.optional(Schema.NullOr(Schema.String)),
});

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Leads RPC group.
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminLeadsRpcs extends RpcGroup.make(
  Rpc.make("create", {
    payload: LeadCreateInput,
    success: Lead.json,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("list", {
    payload: LeadListInput,
    success: Schema.Array(Lead.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("get", {
    payload: LeadByIdInput,
    success: LeadWithPerson,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("update", {
    payload: LeadUpdateInput,
    success: Lead.json,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("convert", {
    payload: LeadConvertInput,
    success: Intake.json,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
).prefix("admin/leads/") {}
